import * as React from 'react';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import PromptModal from 'components/modal/prompt-modal/PromptModal';
import Tooltip from 'components/Tooltip';
import { withModal } from 'components/modal/ModalHOC';

import { declineRealmOutfit, publishRealmOutfit } from 'modules/realm-outfits/reducers/realm-outfits';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';
import {
  ITEMS_PER_OUTFIT,
  REALM_OUTFIT_STATUS_PENDING,
  getValidationError,
  splitTagsByType
} from 'utils/realm-outfits';

import './realm-outfit-review.scss';

import type { RealmOutfit, RealmOutfitTag } from 'types';

applyTranslations(require('./realm-outfit-review.i18n.json'));

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: PromptModal
});

type OwnProps = {
  outfit: RealmOutfit
};

type StateProps = {
  realmOutfitsTags: Array<RealmOutfitTag>,
  isPublishing: boolean,
  realmOutfitAudioTypeEnabled: boolean
};

type DispatchProps = {
  dispatchDeclineOutfit: (outfitId: number, declinedReason: string) => void,
  dispatchPublishOutfit: (outfitId: number) => void,
  dispatchShowConfirmModal: (a: {}) => Promise<void>,
  dispatchHideConfirmModal: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

class RealmOutfitReview extends React.PureComponent<Props> {
  handleApprove = () => {
    const { outfit, dispatchPublishOutfit, dispatchShowConfirmModal, dispatchHideConfirmModal } = this.props;

    dispatchShowConfirmModal({
      message: __t('realm_outfit_review.confirmation_publish.message'),
      onConfirm: () => {
        dispatchPublishOutfit(outfit.id);

        dispatchHideConfirmModal();
      },
      onCancel: () => dispatchHideConfirmModal()
    });
  };

  handleDecline = (declinedReason: string) => {
    const { outfit, dispatchDeclineOutfit } = this.props;
    dispatchDeclineOutfit(outfit.id, declinedReason);
  };

  render() {
    const { isPublishing, outfit, realmOutfitsTags, realmOutfitAudioTypeEnabled } = this.props;
    if (outfit.status !== REALM_OUTFIT_STATUS_PENDING) {
      return null;
    }

    const outfitTags = splitTagsByType(realmOutfitsTags);
    const validationError = getValidationError(outfit, outfitTags.gtl, outfitTags.regular, realmOutfitAudioTypeEnabled);
    const confirmationButton = (
      <ActionButton
        color="primary"
        disabled={Boolean(validationError) || isPublishing}
        disabledWithSpinner={isPublishing}
        onClick={this.handleApprove}
      >
        {__t('realm_outfit_review.button_publish')}
      </ActionButton>
    );

    return (
      <div className="realm-outfit-review__buttons">
        <ActionButtonWithModal
          color="secondary"
          modalContentProps={{
            onSubmit: this.handleDecline,
            title: __t('realm_outfit_review.confirmation_decline.message'),
            label: __t('realm_outfit_review.confirmation_decline.reason_label'),
            value: '',
            multiline: true,
            okBtnText: __t('realm_outfit_review.confirmation_decline.button')
          }}
        >
          {__t('realm_outfit_review.button_decline')}
        </ActionButtonWithModal>
        {
          validationError ?
            <Tooltip
              message={__t(`realm_outfit_review.errors.${validationError}`, {
                minItems: ITEMS_PER_OUTFIT.get().min,
                maxItems: ITEMS_PER_OUTFIT.get().max
              })}
            >
              {confirmationButton}
            </Tooltip> :
            confirmationButton
        }
      </div>
    );
  }
}

const mapStateToProps = ({ realmOutfits, realmOutfitsTags, runtimeConfig }): StateProps => ({
  realmOutfitsTags: realmOutfitsTags.tags,
  isPublishing: realmOutfits.isPublishing,
  realmOutfitAudioTypeEnabled: runtimeConfig.config.realmOutfitAudioTypeEnabled ?? false
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDeclineOutfit: (outfitId, _declinedReason) =>
    dispatch(declineRealmOutfit(outfitId, _declinedReason)),
  dispatchPublishOutfit: (outfitId) =>
    dispatch(publishRealmOutfit(outfitId)),

  dispatchShowConfirmModal: (props) => dispatch(showConfirmModal(props)),
  dispatchHideConfirmModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(RealmOutfitReview);
